import { useState, useMemo, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { get, post, patch } from "@/lib/api";
import { Skeleton } from "@/components/common/EmptyState";
import { StatusBadge } from "@/components/common/StatusBadge";
import { money } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GripVertical, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const lineAmount = (li) => li.qty * li.rate * (1 - (li.discount || 0) / 100);

export default function QuotationBuilder() {
  const { id, qid } = useParams();
  const navigate = useNavigate();
  const [quo, setQuo] = useState(null);

  const { data: lead } = useQuery({ queryKey: ["lead", id], queryFn: () => get(`/crm/leads/${id}`) });
  const { data: existing } = useQuery({
    queryKey: ["quo", qid], enabled: qid !== "new", queryFn: () => get(`/crm/quotations/${qid}`),
  });

  useEffect(() => {
    if (qid === "new" && lead && !quo) {
      setQuo({ lead_id: id, company_name: lead.company_name, status: "draft", currency: "USD", line_items: [] });
    } else if (existing && !quo) setQuo(existing);
  }, [existing, lead, qid, id, quo]);

  const save = useMutation({
    mutationFn: (body) => body.id ? patch(`/crm/quotations/${body.id}`, body) : post("/crm/quotations", body),
    onSuccess: (data) => { setQuo(data); toast.success("Quotation saved"); if (qid === "new") navigate(`/crm/leads/${id}/quotation/${data.id}`, { replace: true }); },
  });

  const totals = useMemo(() => {
    const items = quo?.line_items || [];
    const subtotal = items.reduce((s, li) => s + li.qty * li.rate, 0);
    const discount = items.reduce((s, li) => s + li.qty * li.rate * ((li.discount || 0) / 100), 0);
    const taxed = items.reduce((s, li) => s + lineAmount(li) * ((li.tax || 0) / 100), 0);
    return { subtotal, discount, tax: taxed, total: subtotal - discount + taxed };
  }, [quo]);

  if (!quo) return <Skeleton className="h-96" />;

  const setLine = (idx, key, val) => setQuo((q) => ({ ...q, line_items: q.line_items.map((li, i) => i === idx ? { ...li, [key]: val } : li) }));
  const addLine = () => setQuo((q) => ({ ...q, line_items: [...q.line_items, { id: crypto.randomUUID(), description: "", qty: 1, rate: 0, tax: 10, discount: 0 }] }));
  const delLine = (idx) => setQuo((q) => ({ ...q, line_items: q.line_items.filter((_, i) => i !== idx) }));

  return (
    <div>
      <div className="mb-2 text-xs text-muted-foreground">
        <button onClick={() => navigate(`/crm/leads/${id}`)} className="hover:underline">{quo.company_name}</button> / Quotation
      </div>
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h1 className="font-display text-2xl font-bold tracking-tight">Quotation — {quo.company_name}</h1>
          <StatusBadge status={quo.status} />
        </div>
        <Button data-testid="save-quote" onClick={() => save.mutate(quo)} disabled={save.isPending}>Save Draft</Button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.2fr_1fr]">
        <div className="rounded-xl border border-border bg-card p-4">
          <h3 className="mb-3 font-display text-sm font-semibold">Line Items</h3>
          <div className="space-y-2" data-testid="line-items">
            {quo.line_items.map((li, idx) => (
              <div key={li.id} className="flex items-center gap-2">
                <GripVertical className="h-4 w-4 shrink-0 text-muted-foreground" />
                <Input className="flex-1" placeholder="Description" value={li.description} onChange={(e) => setLine(idx, "description", e.target.value)} data-testid={`li-desc-${idx}`} />
                <Input className="w-16" type="number" value={li.qty} onChange={(e) => setLine(idx, "qty", Number(e.target.value))} title="Qty" />
                <Input className="w-24" type="number" value={li.rate} onChange={(e) => setLine(idx, "rate", Number(e.target.value))} title="Rate" />
                <Input className="w-16" type="number" value={li.discount} onChange={(e) => setLine(idx, "discount", Number(e.target.value))} title="Disc %" />
                <span className="w-24 text-right text-sm font-medium">{money(lineAmount(li), quo.currency)}</span>
                <button onClick={() => delLine(idx)} className="text-muted-foreground hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
              </div>
            ))}
          </div>
          <Button variant="ghost" size="sm" className="mt-2" onClick={addLine} data-testid="add-line"><Plus className="mr-1 h-3.5 w-3.5" />Add Line Item</Button>

          <div className="mt-4 rounded-lg border border-border bg-muted/40 p-3 text-sm">
            <Row label="Subtotal" v={money(totals.subtotal, quo.currency)} />
            <Row label="Discount" v={`- ${money(totals.discount, quo.currency)}`} />
            <Row label="Tax" v={money(totals.tax, quo.currency)} />
            <div className="mt-2 flex justify-between border-t border-border pt-2 font-display text-lg font-bold"><span>Total</span><span>{money(totals.total, quo.currency)}</span></div>
          </div>
        </div>

        <div className="rounded-xl border border-border bg-card p-6">
          <h3 className="mb-4 font-display text-sm font-semibold">Live Preview</h3>
          <div className="rounded-lg border border-border bg-background p-6 text-sm">
            <div className="mb-4 flex items-center justify-between">
              <div className="font-display text-lg font-bold">QUOTATION</div>
              <div className="text-right text-xs text-muted-foreground">Iris Inc.<br />quotes@iris.dev</div>
            </div>
            <div className="mb-4 text-xs text-muted-foreground">Prepared for<br /><span className="text-sm font-semibold text-foreground">{quo.company_name}</span></div>
            <table className="w-full text-xs">
              <thead><tr className="border-b border-border text-left text-muted-foreground"><th className="py-1">Item</th><th className="py-1 text-right">Qty</th><th className="py-1 text-right">Amount</th></tr></thead>
              <tbody>
                {quo.line_items.map((li) => (
                  <tr key={li.id} className="border-b border-border/60"><td className="py-1.5">{li.description || "—"}</td><td className="py-1.5 text-right">{li.qty}</td><td className="py-1.5 text-right">{money(lineAmount(li), quo.currency)}</td></tr>
                ))}
              </tbody>
            </table>
            <div className="mt-3 flex justify-end"><div className="font-display text-base font-bold">Total: {money(totals.total, quo.currency)}</div></div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Row({ label, v }) { return <div className="flex justify-between text-muted-foreground"><span>{label}</span><span className="text-foreground">{v}</span></div>; }
